import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { EmptyState } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useMyStoreQuery, useUpdateStoreMutation } from '../api';
import { StoreForm } from '../components/StoreForm';
import { StoreHealth } from '../components/StoreHealth';
import { StoreVisibilityCard } from '../components/StoreVisibilityCard';
import type { StoreFormValues } from '../schemas';
import { suggestedShopName } from '../slug';

import { NextStepBanner } from '@/components/ui/NextStepBanner';
import { FormSkeleton } from '@/components/ui/skeletons/FormSkeleton';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { ShareStoreCard } from '@/features/share/components/ShareStoreCard';
import { useActiveShop } from '@/features/shops/use-active-shop';
import { ApiError } from '@/lib/api-client';
import { whenDefined } from '@/lib/optional-props';

const EMPTY_OPENING_HOURS: StoreFormValues['openingHours'] = {
  monday: '',
  tuesday: '',
  wednesday: '',
  thursday: '',
  friday: '',
  saturday: '',
  sunday: '',
};

export function StorePage(): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const storeQuery = useMyStoreQuery();
  const updateMutation = useUpdateStoreMutation();
  const shop = useActiveShop();
  const [formError, setFormError] = useState<string | null>(null);
  /** The slug the API refused as already used (409): shown on the slug field, not as a page-level alert. */
  const [takenSlug, setTakenSlug] = useState<string | null>(null);
  const [showNextStep, setShowNextStep] = useState(false);

  /**
   * `PUT /merchant/store` requires `@RequireRole('merchant_owner')`
   * (store.controller.ts) — a Staff account, even one holding
   * `catalog:write`, is refused server-side. The form disables itself
   * with a clear reason rather than letting a Staff member fill it out
   * and discover the 403 only on submit.
   */
  const isOwner = shop.activeMembership?.roleKey === 'merchant_owner';

  async function onSubmit(values: StoreFormValues): Promise<void> {
    setFormError(null);
    setTakenSlug(null);
    // Read BEFORE the mutation resolves — `storeQuery.data` flips from
    // `null` to the created store the moment the mutation's optimistic/
    // refetch cache update lands, so capturing it after `await` would
    // always see the new value and never detect "this was a creation".
    const isFirstSave = storeQuery.data === null;
    try {
      await updateMutation.mutateAsync(values);
      toast.success(t('merchant.store.updated'));
      if (isFirstSave) setShowNextStep(true);
    } catch (error) {
      // The only conflict this endpoint raises is « slug already used by another shop ».
      if (error instanceof ApiError && error.status === 409) {
        setTakenSlug(values.slug.trim().toLowerCase());
        return;
      }
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  if (storeQuery.isPending || shop.isLoading) {
    return (
      <div className="mx-auto max-w-3xl">
        <FormSkeleton fields={8} />
      </div>
    );
  }

  if (storeQuery.isError) {
    return (
      <EmptyState
        title={t('merchant.store.loadError')}
        description={t(resolveErrorMessageKey(storeQuery.error))}
      />
    );
  }

  const store = storeQuery.data;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{t('merchant.nav.store')}</h1>
        <p className="text-sm text-inkFaint">
          {store === null ? t('merchant.store.subtitleNew') : t('merchant.store.subtitleExisting')}
        </p>
      </div>

      {showNextStep ? (
        <NextStepBanner
          title={t('merchant.store.nextStep.title')}
          description={t('merchant.store.nextStep.description')}
          ctaLabel={t('merchant.store.nextStep.cta')}
          to="/categories"
          onDismiss={() => {
            setShowNextStep(false);
          }}
        />
      ) : null}

      {shop.activeMembership !== null ? (
        <StoreVisibilityCard
          tenantStatus={shop.activeMembership.tenantStatus}
          storeSlug={store?.slug ?? null}
        />
      ) : null}

      {store !== null ? <ShareStoreCard context="store" /> : null}

      {store !== null ? <StoreHealth /> : null}

      {!isOwner ? (
        <p className="rounded-md bg-warnSoft px-3 py-2 text-sm text-warn">
          {t('merchant.store.ownerOnlyNotice')}
        </p>
      ) : null}

      {formError !== null ? (
        <p role="alert" className="rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical">
          {formError}
        </p>
      ) : null}

      <StoreForm
        {...whenDefined('store', store ?? undefined)}
        {...whenDefined(
          'defaultValues',
          store !== null
            ? {
                slug: store.slug,
                name: store.name,
                description: store.description,
                tagline: store.tagline,
                address: store.address,
                phone: store.phone,
                // Stored as digits only; shown with a leading "+" so the
                // merchant sees an international number, not a bare integer.
                whatsapp: store.whatsapp === null ? null : `+${store.whatsapp}`,
                aboutTitle: store.aboutTitle,
                aboutBody: store.aboutBody,
                email: store.email,
                openingHours: {
                  ...EMPTY_OPENING_HOURS,
                  ...(store.openingHours as Partial<StoreFormValues['openingHours']>),
                },
                returnPolicy: store.returnPolicy,
                shippingPolicy: store.shippingPolicy,
                termsOfService: store.termsOfService,
                privacyPolicy: store.privacyPolicy,
                seoTitle: store.seoTitle,
                seoDescription: store.seoDescription,
                stockDisplayPolicy: store.stockDisplayPolicy,
                deliveryFee: store.deliveryFee,
                colorPrimary: store.colorPrimary,
                colorSecondary: store.colorSecondary,
                colorAccent: store.colorAccent,
                colorText: store.colorText,
                colorButton: store.colorButton,
                colorBadge: store.colorBadge,
                colorPromo: store.colorPromo,
                colorVariant: store.colorVariant,
                metaPixelId: store.metaPixelId,
                socialFacebook: store.socialFacebook,
                socialInstagram: store.socialInstagram,
                socialTiktok: store.socialTiktok,
                socialYoutube: store.socialYoutube,
                socialX: store.socialX,
                socialLinkedin: store.socialLinkedin,
              }
            : undefined,
        )}
        onSubmit={(values) => {
          void onSubmit(values);
        }}
        isSubmitting={updateMutation.isPending}
        submitLabel={
          store === null ? t('merchant.store.createSubmit') : t('merchant.store.saveSubmit')
        }
        disabled={!isOwner}
        {...whenDefined(
          'suggestedName',
          store === null ? suggestedShopName(shop.activeMembership?.legalName) : undefined,
        )}
        takenSlug={takenSlug}
      />
    </div>
  );
}
